import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Space, message, Upload,
  Card, Row, Col, Statistic, Input, Select, Popconfirm, Image,
  Tooltip,
} from 'antd';
import {
  UploadOutlined, DeleteOutlined, SyncOutlined, MedicineBoxOutlined,
  SearchOutlined, PictureOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, CloseCircleOutlined, CloudDownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { assetAPI, adminAPI } from '../services/api';
import { Tool } from '../types';
import { getToolLogoUrl, getToolShowcaseUrl } from '../utils/toolAssets';

const { Search } = Input;

const statusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  LOCAL:    { color: 'green',   label: '已本地化', icon: <CheckCircleOutlined /> },
  EXTERNAL: { color: 'orange',  label: '外链',     icon: <CloudDownloadOutlined /> },
  PARTIAL:  { color: 'blue',    label: '部分本地', icon: <ExclamationCircleOutlined /> },
  MISSING:  { color: 'red',     label: '缺失',     icon: <CloseCircleOutlined /> },
};

interface AssetHealth {
  total: number;
  local: number;
  external: number;
  partial: number;
  missing: number;
  errors: { toolId: number; name: string; error: string }[];
}

const AdminAssetPage: React.FC = () => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<AssetHealth | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const fetchTools = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminAPI.getTools({
        page,
        limit: pagination.pageSize,
        search: search || undefined,
        asset_status: statusFilter || undefined,
        all_tenants: 'true',
      });
      setTools(res.data.tools || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: res.data.pagination?.total || 0,
      }));
    } catch {
      message.error('加载工具列表失败');
    }
    setLoading(false);
  }, [pagination.pageSize, search, statusFilter]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await assetAPI.getHealth();
      setHealth(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchTools();
    fetchHealth();
  }, [fetchTools, fetchHealth]);

  const handleSync = async (onlyMissing = true) => {
    setSyncing(true);
    message.loading({ content: '正在同步资产...', key: 'sync', duration: 0 });
    try {
      const res = await assetAPI.triggerSync({ onlyMissing, concurrency: 3 });
      const { total, success, failed } = res.data;
      message.success({
        content: `同步完成: ${success}/${total} 成功, ${failed} 失败`,
        key: 'sync',
      });
      fetchTools(pagination.current);
      fetchHealth();
    } catch {
      message.error({ content: '同步失败', key: 'sync' });
    }
    setSyncing(false);
  };

  const handleRepair = async (toolId: number) => {
    try {
      await assetAPI.repair(toolId);
      message.success('修复成功');
      fetchTools(pagination.current);
      fetchHealth();
    } catch {
      message.error('修复失败');
    }
  };

  const handleDeleteLogo = async (toolId: number) => {
    try {
      await assetAPI.deleteLogo(toolId);
      message.success('Logo 已删除');
      fetchTools(pagination.current);
    } catch {
      message.error('删除失败');
    }
  };

  const columns: ColumnsType<Tool> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: 'Logo',
      key: 'logo',
      width: 70,
      render: (_, tool) => {
        const url = getToolLogoUrl(tool);
        return url ? (
          <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 6 }} />
        ) : (
          <div style={{ width: 40, height: 40, background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PictureOutlined style={{ color: '#bfbfbf' }} />
          </div>
        );
      },
    },
    {
      title: '展示图',
      key: 'showcase',
      width: 90,
      render: (_, tool) => {
        const url = getToolShowcaseUrl(tool);
        return url ? (
          <Image src={url} width={72} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} />
        ) : (
          <Tag color="default">无</Tag>
        );
      },
    },
    {
      title: '名称',
      dataIndex: 'name',
      ellipsis: true,
      width: 180,
    },
    {
      title: '资产状态',
      key: 'asset_status',
      width: 100,
      render: (_, tool) => {
        const status = tool.asset_status || 'EXTERNAL';
        const cfg = statusConfig[status] || statusConfig.EXTERNAL;
        return (
          <Tooltip title={tool.asset_error || undefined}>
            <Tag color={cfg.color} icon={cfg.icon}>
              {cfg.label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '最后同步',
      key: 'synced',
      width: 130,
      render: (_, tool) => tool.asset_last_synced_at
        ? new Date(tool.asset_last_synced_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, tool) => (
        <Space size="small">
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={async ({ file }) => {
              try {
                await assetAPI.uploadLogo(tool.id, file as File);
                message.success('Logo 上传成功');
                fetchTools(pagination.current);
              } catch {
                message.error('上传失败');
              }
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>Logo</Button>
          </Upload>
          <Button
            size="small"
            icon={<MedicineBoxOutlined />}
            onClick={() => handleRepair(tool.id)}
            title="重新下载外链资产"
          >
            修复
          </Button>
          {tool.logo_url && (
            <Popconfirm title="确认删除本地 Logo？" onConfirm={() => handleDeleteLogo(tool.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>📦 资产管理</h2>

      {/* 健康统计卡片 */}
      {health && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic title="总计" value={health.total} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="已本地化" value={health.local} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="外链" value={health.external} valueStyle={{ color: '#faad14' }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="部分本地" value={health.partial} valueStyle={{ color: '#1677ff' }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="缺失" value={health.missing} valueStyle={{ color: '#ff4d4f' }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="本地化率"
                value={health.total > 0 ? ((health.local / health.total) * 100).toFixed(1) : 0}
                suffix="%"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 工具栏 */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          placeholder="搜索工具名称"
          allowClear
          onSearch={val => { setSearch(val); fetchTools(1); }}
          style={{ width: 240 }}
        />
        <Select
          placeholder="资产状态"
          allowClear
          style={{ width: 140 }}
          onChange={val => { setStatusFilter(val); }}
          options={[
            { value: 'LOCAL', label: '已本地化' },
            { value: 'EXTERNAL', label: '外链' },
            { value: 'PARTIAL', label: '部分本地' },
            { value: 'MISSING', label: '缺失' },
          ]}
        />
        <Button onClick={() => fetchTools(1)} icon={<SearchOutlined />}>搜索</Button>
        <Button
          type="primary"
          icon={<SyncOutlined spin={syncing} />}
          onClick={() => handleSync(true)}
          loading={syncing}
        >
          同步缺失资产
        </Button>
        <Popconfirm title="确认重新同步全部资产？这可能需要较长时间。" onConfirm={() => handleSync(false)}>
          <Button icon={<SyncOutlined />} loading={syncing}>
            全量同步
          </Button>
        </Popconfirm>
      </Space>

      {/* 工具列表 */}
      <Table
        columns={columns}
        dataSource={tools}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: total => `共 ${total} 条`,
          onChange: (page) => fetchTools(page),
        }}
        size="small"
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default AdminAssetPage;
